// Project-wide date formatting helpers.  All user-facing dates use the
// "d-mmm-yy" shape (e.g. "5-Mar-26", "12-Dec-25") for consistency across
// the tree row date columns, the detail pane, share links, the admin
// panel, and the public viewer.
//
// Native form inputs (<input type="date">) and the Gantt time axis are
// deliberately exempt — the date input requires "YYYY-MM-DD" wire format,
// and the Gantt has its own zoom-aware tick labels.
//
// Inputs accepted:
//   - "YYYY-MM-DD"               (DATE column values)
//   - "YYYY-MM-DDTHH:mm:ss…Z"    (TIMESTAMPTZ-derived ISO strings)
//   - JS Date
//
// For YYYY-MM-DD we deliberately construct a Date pinned to *local*
// midnight rather than UTC, so a due date of 2026-03-05 reads as "5-Mar-26"
// for a user in any timezone — not "4-Mar-26" west of Greenwich.

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function asDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  const head = value.slice(0, 10);
  // "YYYY-MM-DD" with no time component → local midnight, no TZ shift.
  if (/^\d{4}-\d{2}-\d{2}$/.test(head) && value.length === 10) {
    const [y, m, d] = head.split("-").map(Number);
    return new Date(y!, (m ?? 1) - 1, d ?? 1);
  }
  // Otherwise let the platform parse the full string (preserves TZ math).
  return new Date(value);
}

export function formatDmy(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "";
  const d = asDate(value);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getDate();
  const month = SHORT_MONTHS[d.getMonth()] ?? "";
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

/** Same date format with a `HH:MM` time appended.  Falls back to plain
 *  date when value has no clock component (e.g. plain YYYY-MM-DD). */
export function formatDmyTime(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "";
  const date = formatDmy(value);
  if (!date) return "";
  if (value instanceof Date || (typeof value === "string" && value.length > 10)) {
    const d = asDate(value);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${date}, ${hh}:${mm}`;
  }
  return date;
}
